import React, { useState } from 'react';
import type { QueueDiagnostics } from './types';

interface QueueDiagnosticsPanelProps {
    data: QueueDiagnostics | null;
    onRun60s: () => Promise<any>;
    onAbandonStale: (dryRun: boolean) => Promise<any>;
    onDumpQueue: () => Promise<any>;
    minimal?: boolean;
}

export const QueueDiagnosticsPanel: React.FC<QueueDiagnosticsPanelProps> = ({ data, onRun60s, onAbandonStale, onDumpQueue, minimal }) => {
    const [running, setRunning] = useState(false);
    const [advancedOpen, setAdvancedOpen] = useState(false); // Collapsed by default
    const [lastResult, setLastResult] = useState<string | null>(null);

    // If no data yet, show minimal loading or placeholder
    if (!data) return <div style={{ padding: '10px', color: '#666', fontSize: '12px' }}>Loading Diagnostics...</div>;

    const consumerDead = data.consumerAlive === false;
    const beatAge = data.consumerBeatAgeSeconds ?? data.consumerHealth; // Prefer explicit beat age
    const jobsStale = data.runningPreview.some(j => j.ageSeconds > 3600);

    const handleRun60s = async () => {
        setRunning(true);
        setLastResult(null);
        try {
            const res = await onRun60s();
            if (res) {
                setLastResult(`Processed: ${res.processed}, Success: ${res.succeeded}, Failed: ${res.failed} (${res.stopReason})`);
            }
        } catch (e) {
            setLastResult("Error: " + String(e));
        }
        setRunning(false);
    };

    const handleAbandonStale = async (dryRun: boolean) => {
        setRunning(true);
        setLastResult(null);
        try {
            const res = await onAbandonStale(dryRun);
            if (res && res.ok) {
                setLastResult(`${dryRun ? '[DRY] ' : ''}Found ${res.foundStale}, Abandoned: ${res.abandonedCount}`);
            } else {
                setLastResult("Error: " + JSON.stringify(res));
            }
        } catch (e) {
            setLastResult("Error: " + String(e));
        }
        setRunning(false);
    };

    const handleDump = async () => {
        setRunning(true);
        setLastResult(null);
        try {
            const res = await onDumpQueue();
            console.log("Dump:", res);
            setLastResult("Dump logged to console + copied sample to clipboard (simulated)");
            // In a real app we might put it on clipboard.
            if (res?.jobsSample) {
                navigator.clipboard.writeText(JSON.stringify(res, null, 2)).catch(() => { });
            }
        } catch (e) {
            setLastResult("Error: " + String(e));
        }
        setRunning(false);
    };

    return (
        <div style={{ background: '#1e1e1e', border: '1px solid #444', borderRadius: '4px', overflow: 'hidden', padding: '10px' }}>
            {/* Header: Health & Counts */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                    <div style={{ fontWeight: 'bold', fontSize: '13px', color: '#eee' }}>QUEUE DIAGNOSTICS</div>
                    <div style={{
                        fontSize: '11px', fontWeight: 'bold',
                        color: consumerDead ? '#fff' : '#000',
                        background: consumerDead ? '#ef5350' : '#81c784',
                        padding: '2px 6px', borderRadius: '2px'
                    }}>
                        {consumerDead ? `STALLED (${beatAge ?? '?'}s)` : 'OK'}
                    </div>
                </div>

                {/* Legacy Link (conditionally shown if implementation confirms route exists, which App.tsx does) */}
                <a href="/admin/factory" style={{ fontSize: '11px', color: '#64b5f6', textDecoration: 'none' }}>Open legacy queue view</a>
            </div>

            {/* Counts Grid */}
            <div style={{ display: 'flex', gap: '20px', fontSize: '12px', color: '#ccc', marginBottom: '8px' }}>
                <div>Queued: <b style={{ color: '#fff' }}>{data.counts.queued}</b></div>
                <div>Running: <b style={{ color: '#fff' }}>{data.counts.running}</b></div>
                <div>Failed: <b style={{ color: '#fff' }}>{data.counts.failed}</b></div>
                <div>Locks: <b style={{ color: '#fff' }}>{data.locksActiveCount}</b></div>
            </div>

            {/* Warning Banner */}
            {/* Warning Banner: Consumer Dead */}
            {consumerDead && (
                <div style={{ background: '#b71c1c', color: '#fff', fontSize: '12px', padding: '6px', borderRadius: '2px', marginBottom: '8px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span>⚠️ Queue consumer OFF/STALLED ({beatAge}s ago).</span>
                </div>
            )}

            {/* Warning Banner: Stale Jobs (but Consumer Alive) */}
            {jobsStale && !consumerDead && (
                <div style={{ background: '#ff8f00', color: 'black', fontSize: '12px', padding: '6px', borderRadius: '2px', marginBottom: '8px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span>⚠️ Long running jobs detected (&gt;1h).</span>
                </div>
            )}

            {/* Advanced Section */}
            <div style={{ marginTop: '5px' }}>
                <div
                    onClick={() => setAdvancedOpen(!advancedOpen)}
                    style={{ fontSize: '10px', color: '#888', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px' }}
                >
                    {advancedOpen ? '▼ Advanced' : '▶ Advanced'}
                </div>

                {advancedOpen && (
                    <div style={{ marginTop: '8px', padding: '8px', background: '#111', borderRadius: '4px', border: '1px solid #333' }}>
                        {/* Fix Queue Tools */}
                        <div style={{ marginBottom: '10px', borderBottom: '1px solid #333', paddingBottom: '8px' }}>
                            <div style={{ fontSize: '10px', color: '#aaa', marginBottom: '4px', fontWeight: 'bold' }}>FIX QUEUE</div>
                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                <button
                                    onClick={handleRun60s}
                                    disabled={running}
                                    title="Processes queued jobs for up to 60 seconds.&#010;Does not retry failed jobs.&#010;Safe to run anytime."
                                    style={{ background: '#283593', color: '#fff', border: '1px solid #5c6bc0', padding: '4px 8px', fontSize: '11px', cursor: running ? 'default' : 'pointer', borderRadius: '2px' }}
                                >
                                    {running ? '...' : 'Run 60s (Queued only)'}
                                </button>
                                <button
                                    onClick={() => handleAbandonStale(true)}
                                    disabled={running}
                                    title="Checks for stale running jobs (>1h).&#010;Does not modify any data.&#010;Use to test abandonment logic."
                                    style={{ background: '#333', color: '#fff', border: '1px solid #666', padding: '4px 8px', fontSize: '11px', cursor: running ? 'default' : 'pointer', borderRadius: '2px' }}
                                >
                                    Abandon Stale (Dry)
                                </button>
                                <button
                                    onClick={() => handleAbandonStale(false)}
                                    disabled={running}
                                    title="Marks jobs with no progress for >1h as abandoned.&#010;Does NOT delete audio or metadata.&#010;Use when jobs are permanently stuck."
                                    style={{ background: '#b71c1c', color: '#fff', border: '1px solid #ef5350', padding: '4px 8px', fontSize: '11px', cursor: running ? 'default' : 'pointer', borderRadius: '2px' }}
                                >
                                    Abandon Stale (DO IT)
                                </button>
                                <button
                                    onClick={handleDump}
                                    disabled={running}
                                    title="Downloads current queue state as JSON.&#010;Does not change queue.&#010;Use for deep debugging."
                                    style={{ background: '#00695c', color: '#fff', border: '1px solid #4db6ac', padding: '4px 8px', fontSize: '11px', cursor: running ? 'default' : 'pointer', borderRadius: '2px' }}
                                >
                                    Dump JSON
                                </button>
                            </div>
                            {lastResult && (
                                <div style={{ fontSize: '10px', color: '#81c784', fontFamily: 'monospace', marginTop: '4px', whiteSpace: 'pre-wrap' }}>
                                    {lastResult}
                                </div>
                            )}
                        </div>

                        {/* Heartbeat Details */}
                        <div style={{ fontSize: '10px', color: '#888', marginBottom: '8px', fontFamily: 'monospace' }}>
                            <div>Last Beat: {data.heartbeat.lastBeatAt}</div>
                            <div>Consumer: {data.heartbeat.consumer}</div>
                            <div>Phase: {data.heartbeat.phase}</div>
                        </div>

                        {/* Running Preview */}
                        <div style={{ fontSize: '10px', color: '#aaa' }}>
                            <div style={{ borderBottom: '1px solid #333', marginBottom: '4px' }}>RUNNING JOB PREVIEW (Max 3)</div>
                            {data.runningPreview.length === 0 ? (
                                <div style={{ color: '#666' }}>No active jobs tracked by status endpoint.</div>
                            ) : (
                                data.runningPreview.map(job => {
                                    const isStaleSeeded = job.step === 'seeded' && job.ageSeconds > 600 && data.locksActiveCount === 0;
                                    return (
                                        <div key={job.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '2px 0', borderBottom: '1px solid #222' }}>
                                            <span style={{ fontFamily: 'monospace', color: '#fff' }}>{job.id}</span>
                                            <span style={{ color: '#81d4fa' }}>{job.step}</span>
                                            <span style={{ color: job.ageSeconds > 3600 ? '#ef5350' : '#888' }}>{job.ageSeconds}s</span>

                                            {isStaleSeeded && (
                                                <span style={{ background: '#ff9800', color: '#000', fontSize: '9px', fontWeight: 'bold', padding: '0 3px', borderRadius: '2px' }}>STALE</span>
                                            )}

                                            <div style={{ flex: 1 }}></div>

                                            <a
                                                href={`/api/factory/job/${job.id}/status`}
                                                target="_blank"
                                                rel="noreferrer"
                                                style={{ color: '#64b5f6', textDecoration: 'none', opacity: 0.7 }}
                                                title="Open Status JSON"
                                            >
                                                ?
                                            </a>
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
